import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { Activity, ArrowRight, ExternalLink, RefreshCw } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import type { RunDetail } from "@/api/generated/RunDetail"
import type { RunSummary } from "@/api/generated/RunSummary"
import { useAgents } from "@/api/queries/agents"
import { RUN_SOURCES, RUN_STATUSES, type RunFilters, useRuns } from "@/api/queries/runs"
import { createColumns, DataTable } from "@/components/app/data-table"
import { PageContainer, PageHeader } from "@/components/app/page"
import { RelativeTime } from "@/components/app/relative-time"
import { EmptyState } from "@/components/app/states"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { formatCost, formatDuration, formatTokens } from "@/features/runs/format"
import { RunDetailView } from "@/features/runs/run-detail"
import { RunStatusBadge, useSourceLabel } from "@/features/runs/run-status"
import { AgentAvatar } from "@/features/studio/agent-avatar"
import { useDebounced } from "@/features/studio/use-debounced"
import { formatDateTime } from "@/lib/format"

interface RunsSearch {
  page?: number
  size?: number
  q?: string
  agent?: number
  source?: string
  status?: string
  from?: string
  to?: string
  context?: string
  /** uid of the run open in the drawer */
  run?: string
}

const str = (v: unknown) => (typeof v === "string" && v ? v : undefined)
const num = (v: unknown) => {
  const n = Number(v)
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : undefined
}

export const Route = createFileRoute("/_app/insights/runs/")({
  staticData: { titleKey: "nav.items.runs" },
  validateSearch: (s: Record<string, unknown>): RunsSearch => ({
    page: num(s.page),
    size: num(s.size),
    q: str(s.q),
    agent: num(s.agent),
    source: str(s.source),
    status: str(s.status),
    from: str(s.from),
    to: str(s.to),
    context: str(s.context),
    run: str(s.run),
  }),
  component: RunsPage,
})

/** "YYYY-MM-DD" (local) → ISO start of that day; `end` gives the start of the next day (exclusive bound). */
export function dayBound(day: string | undefined, end: boolean): string | undefined {
  if (!day) return undefined
  const d = new Date(`${day}T00:00:00`)
  if (Number.isNaN(d.getTime())) return undefined
  if (end) d.setDate(d.getDate() + 1)
  return d.toISOString()
}

function RunsPage() {
  const { t } = useTranslation()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const sourceLabel = useSourceLabel()
  const page = search.page ?? 1
  const size = search.size ?? 50
  const [query, setQuery] = useState(search.q ?? "")
  const debounced = useDebounced(query, 300)
  const agents = useAgents({ include_archived: true })

  const setSearch = (patch: Partial<RunsSearch>, resetPage = true) =>
    void navigate({
      search: (prev) => ({ ...prev, ...patch, ...(resetPage ? { page: undefined } : {}) }),
      replace: true,
    })

  // biome-ignore lint/correctness/useExhaustiveDependencies: only react to the debounced value
  useEffect(() => {
    if ((debounced || undefined) !== search.q) setSearch({ q: debounced || undefined })
  }, [debounced])

  const filters: RunFilters = {
    limit: size,
    offset: (page - 1) * size,
    q: search.q,
    agent_id: search.agent,
    source: search.source,
    status: search.status,
    context_id: search.context,
    from: dayBound(search.from, false),
    to: dayBound(search.to, true),
  }
  const [live, setLive] = useState(false)
  const runs = useRuns(filters, { refetchInterval: live ? 4000 : false })
  const anyRunning = !!runs.data?.items.some((r) => r.status === "running")
  useEffect(() => setLive(anyRunning), [anyRunning])

  const hasFilters = !!(
    search.q ||
    search.agent ||
    search.source ||
    search.status ||
    search.from ||
    search.to ||
    search.context
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: label helper only depends on t
  const columns = useMemo(
    () =>
      createColumns<RunSummary>((c) => [
        c.accessor("status", {
          header: t("insights.runs.columns.status"),
          enableSorting: false,
          cell: ({ getValue }) => <RunStatusBadge status={getValue()} />,
        }),
        c.accessor("agent_name", {
          header: t("insights.runs.columns.run"),
          enableSorting: false,
          enableHiding: false,
          cell: ({ row }) => {
            const r = row.original
            return (
              <div className="flex max-w-[26rem] min-w-48 items-start gap-2.5">
                <AgentAvatar name={r.agent_name || "?"} size="sm" className="mt-0.5" />
                <div className="min-w-0">
                  <div className="truncate font-medium">
                    {r.agent_name || t("insights.runs.unknownAgent")}
                  </div>
                  <div className="text-muted-foreground truncate text-xs">
                    {r.error ? <span className="text-destructive">{r.error}</span> : r.prompt_preview || "—"}
                  </div>
                </div>
              </div>
            )
          },
        }),
        c.accessor("source", {
          header: t("insights.runs.columns.source"),
          enableSorting: false,
          cell: ({ getValue, row }) => (
            <div className="text-muted-foreground text-[0.8125rem]">
              <div>{sourceLabel(getValue())}</div>
              {row.original.username && <div className="text-xs">{row.original.username}</div>}
            </div>
          ),
        }),
        c.accessor("model_identifier", {
          header: t("insights.runs.columns.model"),
          enableSorting: false,
          cell: ({ getValue }) => (
            <code className="text-muted-foreground block max-w-40 truncate font-mono text-xs">
              {getValue()}
            </code>
          ),
        }),
        c.accessor("total_tokens", {
          header: t("insights.runs.columns.tokens"),
          enableSorting: false,
          meta: { align: "end" },
          cell: ({ row }) => (
            <Tooltip>
              <TooltipTrigger render={<span className="tabular-nums" />}>
                {formatTokens(row.original.total_tokens)}
              </TooltipTrigger>
              <TooltipContent>
                {t("insights.runs.tokensSplit", {
                  input: formatTokens(row.original.input_tokens),
                  output: formatTokens(row.original.output_tokens),
                })}
              </TooltipContent>
            </Tooltip>
          ),
        }),
        c.accessor("cost", {
          header: t("insights.runs.columns.cost"),
          enableSorting: false,
          meta: { align: "end" },
          cell: ({ getValue }) => <span className="tabular-nums">{formatCost(getValue())}</span>,
        }),
        c.accessor("tool_call_count", {
          header: t("insights.runs.columns.tools"),
          enableSorting: false,
          meta: { align: "end" },
          cell: ({ getValue }) => (
            <span className="text-muted-foreground tabular-nums">{getValue() || "—"}</span>
          ),
        }),
        c.accessor("duration_ms", {
          header: t("insights.runs.columns.duration"),
          enableSorting: false,
          meta: { align: "end" },
          cell: ({ getValue, row }) =>
            row.original.status === "running" ? (
              <span className="text-info text-xs">{t("insights.runs.status.running")}</span>
            ) : (
              <span className="tabular-nums">{formatDuration(getValue())}</span>
            ),
        }),
        c.accessor("started_at", {
          header: t("insights.runs.columns.started"),
          enableSorting: false,
          cell: ({ getValue }) => <RelativeTime value={getValue()} className="text-muted-foreground" />,
        }),
      ]),
    [t],
  )

  const [loaded, setLoaded] = useState<RunDetail>()
  const listed = runs.data?.items.find((r) => r.uid === search.run)
  const open = listed ?? (loaded?.uid === search.run ? loaded : undefined)

  return (
    <PageContainer width="full" className="max-w-[100rem]">
      <PageHeader
        title={t("insights.runs.title")}
        description={t("insights.runs.description")}
        icon={Activity}
        actions={
          <Button variant="outline" size="sm" onClick={() => void runs.refetch()} disabled={runs.isFetching}>
            <RefreshCw className={runs.isFetching ? "animate-spin" : undefined} />
            {t("insights.runs.refresh")}
          </Button>
        }
      />
      <DataTable
        label={t("insights.runs.title")}
        data={runs.data?.items}
        columns={columns}
        getRowId={(r) => r.uid}
        loading={runs.isPending}
        error={runs.error}
        onRetry={() => void runs.refetch()}
        searchable
        search={query}
        onSearchChange={setQuery}
        searchPlaceholder={t("insights.runs.search")}
        columnToggle
        persistKey="insights-runs"
        initialColumnVisibility={{ tool_call_count: false }}
        serverPagination={{
          pageIndex: page - 1,
          pageSize: size,
          rowCount: runs.data?.total ?? 0,
          onChange: (p) =>
            setSearch(
              {
                page: p.pageIndex + 1 > 1 ? p.pageIndex + 1 : undefined,
                size: p.pageSize === 50 ? undefined : p.pageSize,
              },
              false,
            ),
        }}
        onRowClick={(r) => setSearch({ run: r.uid }, false)}
        isRowActive={(r) => r.uid === search.run}
        toolbar={
          <>
            <Select
              value={search.agent != null ? String(search.agent) : "all"}
              onValueChange={(v) => setSearch({ agent: v === "all" || v == null ? undefined : Number(v) })}
            >
              <SelectTrigger size="sm" className="h-8 w-40" aria-label={t("insights.runs.columns.agent")}>
                <SelectValue>
                  {(v: string) =>
                    v === "all"
                      ? t("insights.runs.filters.allAgents")
                      : (agents.data?.find((a) => String(a.id) === v)?.name ?? `#${v}`)
                  }
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("insights.runs.filters.allAgents")}</SelectItem>
                {agents.data?.map((a) => (
                  <SelectItem key={a.id} value={String(a.id)}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={search.source ?? "all"}
              onValueChange={(v) => setSearch({ source: v === "all" || v == null ? undefined : v })}
            >
              <SelectTrigger size="sm" className="h-8 w-36" aria-label={t("insights.runs.columns.source")}>
                <SelectValue>
                  {(v: string) => (v === "all" ? t("insights.runs.filters.allSources") : sourceLabel(v))}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("insights.runs.filters.allSources")}</SelectItem>
                {RUN_SOURCES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {sourceLabel(s)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={search.status ?? "all"}
              onValueChange={(v) => setSearch({ status: v === "all" || v == null ? undefined : v })}
            >
              <SelectTrigger size="sm" className="h-8 w-36" aria-label={t("insights.runs.columns.status")}>
                <SelectValue>
                  {(v: string) =>
                    v === "all"
                      ? t("insights.runs.filters.allStatuses")
                      : t(`insights.runs.status.${v}` as "insights.runs.status.running", v)
                  }
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("insights.runs.filters.allStatuses")}</SelectItem>
                {RUN_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {t(`insights.runs.status.${s}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1.5">
              <Input
                type="date"
                aria-label={t("insights.runs.filters.from")}
                value={search.from ?? ""}
                max={search.to}
                onChange={(e) => setSearch({ from: e.target.value || undefined })}
                className="h-8 w-36 text-xs"
              />
              <ArrowRight className="text-muted-foreground size-3.5" aria-hidden />
              <Input
                type="date"
                aria-label={t("insights.runs.filters.to")}
                value={search.to ?? ""}
                min={search.from}
                onChange={(e) => setSearch({ to: e.target.value || undefined })}
                className="h-8 w-36 text-xs"
              />
            </div>
            {hasFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setQuery("")
                  setSearch({
                    q: undefined,
                    agent: undefined,
                    source: undefined,
                    status: undefined,
                    from: undefined,
                    to: undefined,
                    context: undefined,
                  })
                }}
              >
                {t("insights.runs.filters.clear")}
              </Button>
            )}
            {live && (
              <span className="text-info flex items-center gap-1.5 text-xs">
                <span className="bg-info size-1.5 animate-pulse rounded-full" />
                {t("insights.runs.autoRefresh")}
              </span>
            )}
          </>
        }
        emptyState={
          <EmptyState
            variant="plain"
            icon={Activity}
            title={hasFilters ? t("insights.runs.emptyFiltered.title") : t("insights.runs.empty.title")}
            description={
              hasFilters ? t("insights.runs.emptyFiltered.description") : t("insights.runs.empty.description")
            }
          />
        }
      />
      <Sheet open={!!search.run} onOpenChange={(o) => !o && setSearch({ run: undefined }, false)}>
        <SheetContent className="gap-0 sm:max-w-2xl">
          <SheetHeader className="border-b px-5 py-4 pr-12">
            <SheetTitle className="flex items-center gap-2">
              <span className="truncate">{open?.agent_name ?? t("insights.runs.detailTitle")}</span>
              {search.run && (
                <Button
                  variant="ghost"
                  size="icon-xs"
                  nativeButton={false}
                  render={<Link to="/insights/runs/$runId" params={{ runId: search.run }} />}
                  aria-label={t("insights.runs.openPage")}
                  className="text-muted-foreground"
                >
                  <ExternalLink />
                </Button>
              )}
            </SheetTitle>
            <SheetDescription>
              {open ? formatDateTime(open.started_at, "PPpp") : t("common.loading")}
            </SheetDescription>
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
            {search.run && <RunDetailView runKey={search.run} onLoaded={setLoaded} />}
          </div>
        </SheetContent>
      </Sheet>
    </PageContainer>
  )
}
